import { Component, ComponentRef, ElementRef, OnDestroy } from "@angular/core";
import {
  GoldenLayoutService,
  GLPopoutUndockBehaviour,
  GLPopoutConfiguration,
  GLPopoutEvent,
  CONFIG_PROPERTY_POPOUT_COMPONENT_ID,
  GLBadgeUpdateEvent,
} from "./golden-layout-service";
import { GoldenLayoutPopout } from "./golden-layout-popout";
import { LogoutNotificationService } from "app/bfx-auth/logout-notification.service";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);
import { PointTopLeft } from "openfin/_v2/api/system/point";

import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import { Observable, Subscription } from "rxjs";
import { ResizeObserverScheduler } from "shared/helper-services/resize-observer-scheduler";
import { OEScreenContainerComponent } from "../order-entry/interface/oe-screen-container.component";
import { GLComponentAlertStatus } from "./golden-layout-component";
import { BfxAuthService } from "../bfx-common/services/bfx-auth.service";
import { POPOUT_CONTAINER_PROFILE_CONFIGURATION } from "../../shared/cubiq-services/model/golden-layout.constants";
import { NullProtoObject } from "../../shared/helper-services/null-proto-object";
import { NotificationService } from "../../shared/cubiq-services";
import { NativeDesktopWindow } from "shared/native-desktop/NativeDesktopWindow";
import { Rect } from "shared/native-desktop/NativeDesktopData";
import { WindowType } from "shared/native-desktop/NativeDesktopWindowOptions";

export const MIN_Z_INDEX: number = 500;

export const DEFAULT_MIN_VISIBLE_PX = 20;
const DEFAULT_POSITION: any = { x: 30, y: 60 };
const POSITION_INCREMENT: number = 25;
export const ELEMENT_STACK_ID: string = "stackId";
export const ELEMENT_POPOUT_ID: string = "popoutId";

const FLASHING_STATUSES = [
  GLComponentAlertStatus.ALERT_FLASH,
  GLComponentAlertStatus.HCO_FLASH,
  GLComponentAlertStatus.SCO_FLASH,
];

export const containsPoint = (container: Rect, point: PointTopLeft) => {
  return (
    point.left >= container.left &&
    point.top >= container.top &&
    point.top < container.bottom &&
    point.left < container.right
  );
};

@Component({
  selector: "gl-popout-container",
  template: '<div style="max-height: 0px"></div>',
  standalone: false,
})
export class GoldenLayoutPopupContainer implements OnDestroy {
  private popouts: NullProtoObject<ComponentRef<GoldenLayoutPopout>> = Object.create(null);
  private popoutLogoutObserver: NullProtoObject<Observable<any>> = Object.create(null);
  private badgeComponents: any = {};

  public resizeObserver: ResizeObserverScheduler;

  private goldenLayoutServiceBadgeSubscription: Subscription = null;
  private goldenLayoutServiceAlertStatusSubscription: Subscription = null;

  constructor(
    private goldenLayoutService: GoldenLayoutService,
    private logoutNotificationService: LogoutNotificationService,
    public element: ElementRef,
    protected authService: BfxAuthService,
    protected notificationService: NotificationService,
  ) {
    this.goldenLayoutService.goldenLayoutPopoutContainer = this;
    this.initializeResizeObserver();
    NativeDesktopSupport.initializationSubject.subscribe(() => {
      this.goldenLayoutServiceBadgeSubscription = goldenLayoutService.subjectBadge.subscribe(
        this.onBadgeUpdate.bind(this),
      );
      this.goldenLayoutServiceAlertStatusSubscription =
        this.goldenLayoutService.subjectFlash.subscribe(this.updateAlertStatus.bind(this));
    });
  }

  protected initializeResizeObserver(): void {
    if (this.resizeObserver == null) {
      this.resizeObserver = new ResizeObserverScheduler((entries) => {
        for (const entry of entries) {
          let target: Element = entry.target;
          if (
            target["nativeWindowComponent"] != null &&
            target["nativeWindowComponent"].resize &&
            target["nativeWindowComponent"].viewInitComplete
          ) {
            target["nativeWindowComponent"].resize(null);
          }
        }
      }, this.constructor.name);
    }
  }

  ngOnDestroy(): void {
    if (this.goldenLayoutServiceBadgeSubscription != null)
      this.goldenLayoutServiceBadgeSubscription.unsubscribe();
    if (this.goldenLayoutServiceAlertStatusSubscription != null)
      this.goldenLayoutServiceAlertStatusSubscription.unsubscribe();
    if (this.resizeObserver != null) {
      this.resizeObserver.complete();
      this.resizeObserver = null;
    }
  }

  public getPopoutComponent(popoutComponentId: string): ComponentRef<any> {
    let popoutComponent: ComponentRef<GoldenLayoutPopout> = this.popouts[popoutComponentId];
    let embeddedComponent: ComponentRef<any> = null;

    if (popoutComponent) {
      embeddedComponent = popoutComponent.instance.getEmbeddedComponent();
    }

    return embeddedComponent;
  }

  public onPopoutEvent(event: GLPopoutEvent): void {
    let data: any = { inputs: {} };
    let config: any = event.stack ? event.stack._activeContentItem.config : event.config;
    let popoutConfig: GLPopoutConfiguration = Object.assign(new GLPopoutConfiguration(), config);
    let popoutRef: ComponentRef<GoldenLayoutPopout> = event.popout;
    let popout: GoldenLayoutPopout = popoutRef.instance;
    let undock: boolean = false;
    const eventInstance = event.component.instance;

    if (popoutConfig.glInitError != null) {
      logger.out.error(
        `${event.component.componentType.name} glInit() error: %O`,
        popoutConfig.glInitError,
      );
      event.component.destroy();
      event.popout.destroy();
      let msg = this.notificationService.translationService.instant("error.loading.generic", [
        popoutConfig.glInitError.message,
      ]);
      let title: string =
        this.notificationService.translationService.instant("error.loading.title");
      this.notificationService.handleGenericError(popoutConfig.glInitError, msg, title);
      return;
    }

    if (event.component.instance["persistEmbeddedComponentConfigData"]) {
      let { componentName } = config;
      let {
        [POPOUT_CONTAINER_PROFILE_CONFIGURATION]: {
          [componentName]: popoutContainerConfig = {},
        } = {},
      }: any = this.goldenLayoutService.getAdditionalProfileConfiguration();
      let { data: configData = {} } = config;

      config.data = Object.assign(configData, popoutContainerConfig);
    }

    if (event.component.instance["undocked"] && !NativeDesktopSupport.isNativeDesktopContainer()) {
      event.component.instance["undocked"](event);
    }

    popout.afterViewCheckedReady.then(() => {
      popout
        .embedComponent(
          event.component,
          config,
          this.onPopoutDock.bind(this),
          this.onPopoutClose.bind(this),
          this.onPopoutFocus.bind(this),
          this.onPopoutSaveLayout.bind(this),
        )
        .then(() => {
          popout.componentId = config[CONFIG_PROPERTY_POPOUT_COMPONENT_ID];
          if (typeof eventInstance["getBadgeNumber"] === "function") {
            popout.badgeCount = eventInstance?.getBadgeNumber();
          } else {
            logger.out.info("gl-poc - No badge count defined for view");
          }
          if (typeof eventInstance["getAlertStatus"] === "function") {
            popout.updateAlertStatus(eventInstance.getAlertStatus());
          } else {
            logger.out.info("gl-poc - No alert status doun for view");
          }

          let width: number = popoutConfig.preferredSize ? popoutConfig.preferredSize.width : null;
          let height: number = popoutConfig.preferredSize
            ? popoutConfig.preferredSize.height
            : null;
          let title: string = config ? config.title : "";

          if (event.stack) {
            if (
              popoutConfig.undockBehaviour == GLPopoutUndockBehaviour.INHERIT_FROM_DOCK_CONTAINER
            ) {
              width = event.stack._activeContentItem.container.width;
              height = event.stack._activeContentItem.container.height;
            }
            popout.title = event.stack._activeContentItem.config.title;
            undock = true;
            if (NativeDesktopSupport.isNativeDesktopContainer()) {
              popoutConfig.position = {
                x: window.screenLeft + 50,
                y: window.screenTop + 50,
              };
              while (
                NativeDesktopSupport.getAllOpenWindows()
                  .map((w) => w.getNativeWindow())
                  .find((wnd) => {
                    wnd.screenLeft == popoutConfig.position.x &&
                      wnd.screenTop == popoutConfig.position.y;
                  }) != null
              ) {
                popoutConfig.position.x += 35;
                popoutConfig.position.y += 35;
              }
            }

            config[ELEMENT_STACK_ID] = event.stack.id;
            event.stack._activeContentItem.instance = null;
            event.stack._activeContentItem.remove();
          }

          if (
            popoutConfig.position == null ||
            (typeof popoutConfig.position === "object" &&
              typeof popoutConfig.position.x !== "number") ||
            typeof popoutConfig.position.y !== "number"
          ) {
            let position: any = this.determineNextChildPosition();
            popout.setPosition(position.x, position.y, MIN_Z_INDEX, width, height);
          } else {
            popout.setPosition(
              popoutConfig.position.x,
              popoutConfig.position.y,
              MIN_Z_INDEX,
              width,
              height,
            );
          }

          this.popoutLogoutObserver[popout.componentId] =
            this.logoutNotificationService.getLogoutNotification();
          let popoutCloseFn = (v) => {
            //this is just a temporary fix for entry window to close without dialogs
            popout.closeFromLogout = true;
            let closed = this.onPopoutClose(popout.componentId, true);

            v.res(closed);

            if (!closed) {
              this.popoutLogoutObserver[popout.componentId] =
                this.logoutNotificationService.getLogoutNotification();
              this.popoutLogoutObserver[popout.componentId].subscribe(popoutCloseFn);
            }
          };

          this.popoutLogoutObserver[popout.componentId].subscribe({
            next: popoutCloseFn,
            error: (err?) => {
              logger.out.info(`${this.constructor.name} error in logout observer %O`, err);
            },
            complete: () => {
              if (this.popouts[popout.componentId]) {
                this.onPopoutClose(popout.componentId, true);
              }
            },
          });

          popout.title = title;
          popout.resizable = popoutConfig.popoutResizable;
          popout.resizableDirections = popoutConfig.popoutResizableDirections;

          // attach resize observer
          let rootNodeElement: Element = popoutRef.location.nativeElement
            .firstElementChild as Element;
          let goldenLayoutPopoutComponent: GoldenLayoutPopout =
            popoutRef.instance as GoldenLayoutPopout;
          rootNodeElement["nativeWindowComponent"] =
            goldenLayoutPopoutComponent.embeddedComponent.instance;
          goldenLayoutPopoutComponent.resizeObservedElement = rootNodeElement;

          // TODO ELECTRON (do we need this?)
          // this.resizeObserver.observe(rootNodeElement);

          // Order entry keyboard events
          // If Browser attach here, Desktop is attached after openWindow(), its a desktop timing issue
          if (!NativeDesktopSupport.isNativeDesktopContainer()) {
            this.attachOrderEntryKeyUpEvents(
              popout,
              rootNodeElement["nativeWindowComponent"],
              false,
            );
          }

          if (
            NativeDesktopSupport.isNativeDesktopContainer() &&
            popoutConfig != null &&
            popoutConfig.sourceEvent != null &&
            event.stack == null &&
            popoutConfig.ignoreScreenEvent !== true
          ) {
            let event: MouseEvent = popoutConfig.sourceEvent;

            if (
              popoutConfig.sourceEvent.event != null &&
              popoutConfig.sourceEvent.event.screenX &&
              popoutConfig.sourceEvent.event.screenY
            ) {
              event = popoutConfig.sourceEvent.event;
            } else if (
              popoutConfig.sourceEvent.originalEvent != null &&
              popoutConfig.sourceEvent.originalEvent.screenX &&
              popoutConfig.sourceEvent.originalEvent.screenY
            ) {
              event = popoutConfig.sourceEvent.originalEvent;
            }

            popout["location"] = { x: event.screenX + 10, y: event.screenY + 10 };
          }

          if (NativeDesktopSupport.isNativeDesktopContainer()) {
            popoutRef.location.nativeElement.style.opacity = 0;
            popoutRef.location.nativeElement.firstElementChild.style.opacity = 0;

            let windowType: WindowType = popout.resizable
              ? WindowType.NormalResizable
              : WindowType.Normal;

            NativeDesktopSupport.openWindow(
              popout.title,
              windowType,
              popoutConfig["allowMultiples"],
              popoutConfig,
              (childWindow: NativeDesktopWindow) => {
                popout.nativeWindow = childWindow;
                childWindow[ELEMENT_POPOUT_ID] = popout.componentId;
                popoutRef["window"] = childWindow;
                if (popout.position.x == null) {
                  popout.position.x = popout.position.y = 0;
                }
                if (undock) {
                  // never restore main window on undock.
                  popoutConfig.restoreMainWindow = false;
                }

                popoutRef.changeDetectorRef.detectChanges();

                this.adjustWindow(
                  childWindow,
                  popout,
                  popoutRef,
                  { width: width, height: height },
                  popoutConfig,
                );

                let embeddedComponent: ComponentRef<any> =
                  popoutRef.instance.getEmbeddedComponent();
                if (embeddedComponent.instance != null && embeddedComponent.instance["undocked"]) {
                  embeddedComponent.instance["undocked"]();
                }

                if (
                  embeddedComponent?.instance != null &&
                  embeddedComponent.instance["onDesktopOpen"]
                ) {
                  try {
                    embeddedComponent.instance["onDesktopOpen"](childWindow);
                  } catch (e) {
                    logger.out.error(
                      `${this.constructor.name}: Error invoking onDesktopOpen` +
                        ` with popout ref ${popoutRef}: ${e}`,
                      e,
                    );
                  }
                }

                popout.resizable = false;

                // Order entry keyboard events
                if (
                  rootNodeElement["nativeWindowComponent"] instanceof OEScreenContainerComponent
                ) {
                  this.attachOrderEntryKeyUpEvents(
                    popout,
                    rootNodeElement["nativeWindowComponent"],
                    true,
                  );
                }
              },
            );

            this.popouts[popout.componentId] = popoutRef;
          } else {
            popout.show = true;
            this.popouts[popout.componentId] = popoutRef;
            setTimeout(() => {
              let priorityComponentId = popout.componentId;
              let priorityComponent = popoutRef.instance.embeddedComponent;
              this.reorderChildren(priorityComponentId, priorityComponent);
            }, 0);
          }
        });
    });
  }

  /* Replacing order entry host listeners */
  private attachOrderEntryKeyUpEvents(
    popoutTarget: GoldenLayoutPopout,
    oeScreenComponent: OEScreenContainerComponent,
    isDesktop: boolean,
  ): void {
    if (
      popoutTarget instanceof GoldenLayoutPopout &&
      oeScreenComponent instanceof OEScreenContainerComponent
    ) {
      if (typeof popoutTarget.keyupListener === "function") {
        popoutTarget.keyupListener();
      }
      const keyupFn = (event) => {
        if (event.keyCode == 13 && event.ctrlKey) {
          // Ctrl + Enter
          oeScreenComponent.panelActions.submit();
        } else if (event.keyCode == 9) {
          // Tab
          oeScreenComponent.updateStrategyPanelSize();
        }
      };

      if (isDesktop) {
        popoutTarget.keyupListener = () => {
          popoutTarget.popoutContainer.element.nativeElement.ownerDocument.removeEventListener(
            "keyup",
            keyupFn,
          );
        };
        popoutTarget.popoutContainer.element.nativeElement.ownerDocument.addEventListener(
          "keyup",
          keyupFn,
        );
      } else {
        popoutTarget.keyupListener = () => {
          popoutTarget.popoutContainer.element.nativeElement.removeEventListener("keyup", keyupFn);
        };
        popoutTarget.popoutContainer.element.nativeElement.addEventListener("keyup", keyupFn);
      }
    }
  }

  protected restoreWindow(
    window: NativeDesktopWindow,
    element: Element,
    popoutConfig: GLPopoutConfiguration,
  ): void {
    type callback = () => void;

    const restoreCallback = (callback: callback | null = null) => {
      let rect: any = element.getBoundingClientRect();
      if (popoutConfig.popoutResizable && rect.width > 0 && rect.height > 0) {
        let w: number = rect.width * NativeDesktopSupport.zoomLevel;
        let h: number = rect.height * NativeDesktopSupport.zoomLevel;
        window.setSize(w, h).then(() => {
          if (callback != null) {
            callback();
          }
          this.goldenLayoutService.popoutNativeShown.next();
        });
      } else {
        if (callback != null) {
          callback();
        }
        this.goldenLayoutService.popoutNativeShown.next();
      }
    };

    if (popoutConfig.restoreMainWindow === true) {
      popoutConfig.restoreMainWindow = false;
      restoreCallback(() => {
        this.restoreMainWindow();
      });
    } else {
      window.restoreToFront().then(() => {
        restoreCallback();
      });
    }
  }

  protected restoreMaximizeWindow(window: NativeDesktopWindow, popoutConfig: any): void {
    window.maximize().then(() => {
      if (popoutConfig.preventActivation === true) {
        popoutConfig.preventActivation = false;
        this.restoreMainWindow();
      }
      this.goldenLayoutService.popoutNativeShown.next();
    });
  }

  protected restoreMainWindow(): void {
    let mainWnd: NativeDesktopWindow = NativeDesktopSupport.getNativeDesktopWindow();
    if (mainWnd != null) {
      mainWnd.restoreToFront();
    }
  }

  protected adjustWindow(
    window: NativeDesktopWindow,
    popout: GoldenLayoutPopout,
    component,
    dimensions: any,
    popoutConfig: any,
  ): void {
    let rootNode: ChildNode = component.location.nativeElement;
    let rootNodeElement: Element = rootNode instanceof Element ? rootNode.firstElementChild : null;

    if (rootNodeElement == null) {
      logger.out.error(`${this.constructor.name}.adjustWindow() failed to establish root node`);
      return;
    }

    let width: number =
      dimensions.width != null && dimensions.width > 0
        ? dimensions.width
        : rootNodeElement["offsetWidth"];
    let height: number =
      dimensions.height != null && dimensions.height > 0
        ? dimensions.height
        : rootNodeElement["offsetHeight"];

    let resizable: boolean = popout.resizable;
    if (!resizable) {
      NativeDesktopSupport.attachResizeObserver(rootNodeElement, window);
    }

    NativeDesktopSupport.attachCloseObserver(window, popout.close.bind(popout));

    let x: number = popout["location"] ? popout["location"].x : popout.position.x;
    let y: number = popout["location"] ? popout["location"].y : popout.position.y;

    popout.position.x = 0;
    popout.position.y = 0;

    let restoreIsMaximize: boolean = false;
    let restoreConfig: any = null;
    let winConfig: any = popout.embeddedComponent["_config"];

    if (winConfig != null && !winConfig.allowMultiples) {
      restoreConfig = this.goldenLayoutService.getSingleInstanceConfig(winConfig.componentName);
      if (restoreConfig != null) {
        restoreConfig.docked = false;
      }
    }

    if (restoreConfig == null && winConfig != null) {
      restoreConfig = winConfig.windowConfig;
    }

    if (
      restoreConfig != null &&
      !(
        restoreConfig?.state === "minimized" &&
        NativeDesktopSupport.isWindowConfigOutOfBounds(restoreConfig)
      )
    ) {
      x = restoreConfig.left;
      y = restoreConfig.top;

      if (popoutConfig.popoutResizable) {
        // Otherwise we need to let the window calculate it's own dimensions
        width = restoreConfig.width;
        height = restoreConfig.height;
      }
      if (restoreConfig.stackId != null && winConfig.stackId == null) {
        winConfig.stackId = restoreConfig.stackId;
      }
      restoreIsMaximize = restoreConfig.state === "maximized";
    }

    window.getNativeWindow().document.body.appendChild(rootNodeElement);

    if (!resizable) {
      width = width * NativeDesktopSupport.zoomLevel;
      height = height * NativeDesktopSupport.zoomLevel;
    }

    window.getOptions().then((info: any) => {
      if ((resizable || width === 0) && info.minWidth != null && info.minWidth > width) {
        width = info.minWidth;
      }

      if ((resizable || height === 0) && info.minHeight != null && info.minHeight > height) {
        height = info.minHeight;
      }

      window.setSize(width, height).then(
        () => {
          // Now we figure out the initial location of the window - this is normally based on the previously stored
          // location but if the screen has changed in the interim we need to move so window remains visible
          width = Math.max(width, DEFAULT_MIN_VISIBLE_PX);
          height = Math.max(height, DEFAULT_MIN_VISIBLE_PX);

          rootNodeElement["style"].opacity = 1;

          if (resizable) {
            rootNodeElement["style"].width = "100%";
            rootNodeElement["style"].height = "100%";
          }
          popout.show = true;

          let monitorInfo = NativeDesktopSupport.getMonitorInfo();
          let rect = monitorInfo.virtualScreen;

          x = Math.max(x, rect.left);
          y = Math.max(y, rect.top);

          if (x + width >= rect.right) {
            x = rect.right - width;
          }

          if (y + height + 10 >= rect.bottom) {
            y = rect.bottom - height;
          }

          let { screenRects, availableRects } = NativeDesktopSupport.getActiveRect(monitorInfo);

          let windowLocation: PointTopLeft = { left: x, top: y };
          for (let i: number = 0; i < screenRects.length; i++) {
            if (containsPoint(screenRects[i], windowLocation)) {
              let rect = availableRects[i];

              x = Math.max(x, rect.left);
              y = Math.max(y, rect.top);

              if (x + width >= rect.right) {
                x = rect.right - width;
              }

              if (y + height + 10 >= rect.bottom) {
                y = rect.bottom - height;
              }
            }
          }
          if (x == null || y == null || isNaN(x) || isNaN(y)) {
            logger.out.warn(`gl-pc - adjustWindow x/y error (${x}, ${y}) - resetting to (0,0)`);
            x = y = 0;
          }

          setTimeout(() => {
            window.showAt(x, y).then(() => {
              if (restoreIsMaximize) {
                this.restoreMaximizeWindow(window, popoutConfig);
              } else {
                this.restoreWindow(window, rootNodeElement, popoutConfig);
              }
            });
          }, 0);
        },
        (reason: string) => {
          logger.out.error(
            "Failed to resize window to (",
            width,
            ",",
            height,
            ") reason: ",
            reason,
          );
        },
      );
    });
  }

  protected onPopoutDock(popoutComponentId: string) {
    let popoutComponent: ComponentRef<GoldenLayoutPopout> = this.popouts[popoutComponentId];

    if (popoutComponent) {
      let embeddedComponent: ComponentRef<any> = popoutComponent.instance.getEmbeddedComponent();

      if (
        embeddedComponent.instance["docked"] &&
        !NativeDesktopSupport.isNativeDesktopContainer()
      ) {
        embeddedComponent.instance["docked"](event);
      }

      if (popoutComponent["window"] != null) {
        let window: NativeDesktopWindow = popoutComponent["window"] as NativeDesktopWindow;

        let componentConfig: any = embeddedComponent["_config"];
        if (componentConfig != null && !componentConfig.allowMultiples) {
          NativeDesktopSupport.getWindowConfig(window).then((config: any) => {
            config.stackId = componentConfig.stackId;
            config.docked = true;
            config.asMinimizedConfig = null;
            popoutComponent.instance.asMinimizedConfig = null;
            this.goldenLayoutService.storeSingleInstanceConfig(
              componentConfig.componentName,
              config,
            );
            window.close();
          });
        } else {
          window.close();
        }
      }

      let embeddedComponentConfig: any = popoutComponent.instance.getEmbeddedComponentConfig();
      popoutComponent.instance.embeddedComponent = null;
      popoutComponent.destroy();
      delete this.popouts[popoutComponentId];
      this.goldenLayoutService.dock(popoutComponentId, embeddedComponentConfig);
    }
  }

  protected onPopoutClose(popoutComponentId: string, fromLogout: boolean = false): boolean {
    let component: ComponentRef<GoldenLayoutPopout> = this.popouts[popoutComponentId];

    if (component == null) {
      try {
        delete this.popouts[popoutComponentId];
        delete this.popoutLogoutObserver[popoutComponentId];
        // below is just a delete internal to service
        if (!fromLogout) {
          this.goldenLayoutService.closeComponent(popoutComponentId);
        }
      } catch (e) {
        logger.out.warn(
          `${this.constructor.name}.onPopoutClose cleanup reference missing component`,
        );
      }
      return true;
    }

    if (component.instance == null) {
      try {
        NativeDesktopSupport.detachResizeObserver(component);
        delete this.popouts[popoutComponentId];
        delete this.popoutLogoutObserver[popoutComponentId];
        if (!fromLogout) {
          this.goldenLayoutService.closeComponent(popoutComponentId);
          component.destroy();
        }
      } catch (e) {
        logger.out.warn(
          `${this.constructor.name}.onPopoutClose cleanup reference missing component instance`,
        );
      }
      return true;
    }

    // remove resize observer
    try {
      if (component.instance.embeddedComponent.location.nativeElement.firstElementChild != null) {
        let rootNodeElement: Element = component.instance.embeddedComponent.location.nativeElement
          .firstElementChild as Element;
        if (rootNodeElement instanceof Element) {
          this.resizeObserver.unobserve(rootNodeElement);
        }
        let goldenLayoutPopoutComponent: GoldenLayoutPopout = component.instance;
        if (goldenLayoutPopoutComponent.resizeObservedElement != null) {
          if (component.instance.resizeObservedElement instanceof Element) {
            this.resizeObserver.unobserve(component.instance.resizeObservedElement);
          }
          delete goldenLayoutPopoutComponent.resizeObservedElement["nativeWindowComponent"];
          component.instance.resizeObservedElement = null;
        }
      }
    } catch (e) {
      logger.out.warn(`WARN: Failed to detach resize observer: ${e.message}`);
      logger.out.warn(e.stack);
    }

    let embeddedComponent: ComponentRef<any> = component.instance.getEmbeddedComponent();
    let embeddedOnClose = !!embeddedComponent.instance["onClose"];
    let closed = false;

    if (embeddedOnClose) {
      closed = embeddedComponent.instance["onClose"]();
      closed = closed !== false;
    } else {
      // no onClose callback function should be considered as closed to prevent additional logout notification registration
      closed = true;
    }

    if (closed) {
      if (component["window"] != null) {
        let window: NativeDesktopWindow = component["window"] as NativeDesktopWindow;
        let componentConfig: any = embeddedComponent["_config"];
        if (componentConfig != null && !componentConfig.allowMultiples) {
          NativeDesktopSupport.getWindowConfig(window).then((config: any) => {
            config.stackId = componentConfig.stackId;
            config.docked = false;
            config.asMinimizedConfig = embeddedComponent.instance.asMinimizedConfig;
            this.goldenLayoutService.storeSingleInstanceConfig(
              componentConfig.componentName,
              config,
            );
            window.close();
          });
        } else {
          window.close();
        }
        /**
         * the code below is a specific fix for https://www.bcdfx.com/ijira/browse/OMS-28939
         * in reality it should refactored
         * when opening logic for calendar window is refactored also.
         */
        NativeDesktopSupport.getAllOpenWindows().forEach((wnd) => {
          let windowName = wnd["name"];
          if (windowName === "calendar") {
            wnd.close();
          }
        });
      }

      if (embeddedComponent.instance["persistEmbeddedComponentConfigData"]) {
        let embeddedComponentConfig: any = component.instance.getEmbeddedComponentConfig();

        if (
          !this.goldenLayoutService.getAdditionalProfileConfiguration()["popoutContainerConfigs"]
        ) {
          this.goldenLayoutService.getAdditionalProfileConfiguration()["popoutContainerConfigs"] =
            {};
        }

        if (
          embeddedComponentConfig === Object(embeddedComponentConfig) &&
          typeof embeddedComponentConfig.componentName === "string" &&
          embeddedComponentConfig.data === Object(embeddedComponentConfig.data)
        ) {
          try {
            // Verify that profile configuration can be saved
            JSON.stringify(embeddedComponentConfig.data);

            this.goldenLayoutService.getAdditionalProfileConfiguration()["popoutContainerConfigs"][
              embeddedComponentConfig.componentName
            ] = embeddedComponentConfig.data;
          } catch (error) {
            logger.out.error(
              "Failed to save additional profile configuration for component ",
              embeddedComponentConfig.componentName,
              error,
            );
          }
        }
      }

      //this is just a temporary fix for entry window to close without dialogs
      component.instance.closeFromLogout = false;
      delete this.popouts[popoutComponentId];

      this.logoutNotificationService.deregisterLogoutNotification(
        this.popoutLogoutObserver[popoutComponentId],
      );
      delete this.popoutLogoutObserver[popoutComponentId];

      if (!fromLogout) {
        this.goldenLayoutService.closeComponent(popoutComponentId);
        component.destroy();
      }
    }

    return closed;
  }

  protected onPopoutSaveLayout(popoutComponentId: string): void {
    let component: ComponentRef<GoldenLayoutPopout> = this.popouts[popoutComponentId];
    if (!component) {
      logger.out.info("Not saving popout layout - no stored instance found");
      return;
    }
    // if (component.instance == null) {
    //     try {
    //         delete this.popouts[popoutComponentId];
    //         delete this.popoutLogoutObserver[popoutComponentId];
    //         this.goldenLayoutService.closeComponent(popoutComponentId);
    //         component.destroy();
    //     } catch (e) {
    //         logger.out.warn(`${this.constructor.name}.onPopoutSaveLayout cleanup reference missing instance`);
    //     }
    // }

    let embeddedComponent: ComponentRef<any> = component.instance.getEmbeddedComponent();
    if (embeddedComponent && embeddedComponent.instance["persistEmbeddedComponentConfigData"]) {
      let embeddedComponentConfig: any = component.instance.getEmbeddedComponentConfig();

      if (!this.goldenLayoutService.getAdditionalProfileConfiguration()["popoutContainerConfigs"]) {
        this.goldenLayoutService.getAdditionalProfileConfiguration()["popoutContainerConfigs"] = {};
      }

      if (
        embeddedComponentConfig === Object(embeddedComponentConfig) &&
        typeof embeddedComponentConfig.componentName === "string" &&
        embeddedComponentConfig.data === Object(embeddedComponentConfig.data)
      ) {
        try {
          // Verify that configuration can be saved
          JSON.stringify(embeddedComponentConfig.data);

          this.goldenLayoutService.getAdditionalProfileConfiguration()["popoutContainerConfigs"][
            embeddedComponentConfig.componentName
          ] = embeddedComponentConfig.data;
        } catch (error) {
          logger.out.error(
            "Error saving additional component configuration for component ",
            embeddedComponentConfig.componentName,
            error,
          );
        }
      }
    }
  }

  public onPopoutFocus(
    popoutComponentId: string,
    popoutComponent: ComponentRef<any>,
    popout?: GoldenLayoutPopout,
  ): void {
    Promise.resolve<void>(popout?.afterViewCheckedReady).then(() => {
      this.reorderChildren(popoutComponentId, popoutComponent);
    });
  }
  public updateAlertStatus(event: any): void {
    logger.out.error("DEV-TEST: Alert status update: ", event);
  }

  public onBadgeUpdate(event: GLBadgeUpdateEvent): void {
    (event.component as any).badgeNumber = event.badgeCount;
    (event.component as any).alertStatusTemp = event.status;
    let componentName: string = event.component.constructor.name;

    let badgeCountIncreased: boolean =
      this.badgeComponents[componentName] && this.badgeComponents[componentName] < event.badgeCount;
    let popoutIsFlashing: boolean = false;
    this.badgeComponents[componentName] = event.badgeCount;

    let matchedPopoutIds: string[] = Object.keys(this.popouts).filter(
      (key: string) => this.popouts[key].instance.embeddedComponent.instance == event.component,
    );
    if (matchedPopoutIds.length > 0) {
      let popoutId: string = matchedPopoutIds[0];
      let popoutComponentRef: ComponentRef<GoldenLayoutPopout> = this.popouts[popoutId];
      if (popoutComponentRef != null) {
        let popoutComponent: GoldenLayoutPopout = popoutComponentRef.instance;
        badgeCountIncreased = popoutComponent.badgeCount < event.badgeCount;
        let badgeCountZero: boolean = popoutComponent.badgeCount == 0;
        popoutComponent.badgeCount = event.badgeCount;

        if (popoutComponent.nativeWindow != null) {
          if (this.shouldItFlash(badgeCountIncreased, event.status)) {
            popoutComponent.nativeWindow.flash();
          }
          if (
            (badgeCountZero && !FLASHING_STATUSES.includes(event.status)) ||
            event.status === GLComponentAlertStatus.NONE
          ) {
            popoutComponent.nativeWindow.stopFlashing();
          }
          popoutIsFlashing = true;
        }

        if (event.status != null) {
          popoutComponent.updateAlertStatus(event.status);
        }
      }
    }

    if (!popoutIsFlashing) {
      if (this.shouldItFlash(badgeCountIncreased, event.status)) {
        NativeDesktopSupport.getNativeDesktopWindow().flash();
      } else if (event.badgeCount === 0 || event.status === GLComponentAlertStatus.NONE) {
        NativeDesktopSupport.getNativeDesktopWindow().stopFlashing();
      }
    }
  }

  private shouldItFlash(badgeCountIncreased: boolean, newStatus: GLComponentAlertStatus): boolean {
    if (typeof badgeCountIncreased === "boolean" && newStatus in GLComponentAlertStatus) {
      return (
        (badgeCountIncreased && newStatus === GLComponentAlertStatus.ALERT) ||
        FLASHING_STATUSES.includes(newStatus)
      );
    }
    return false;
  }

  protected reorderChildren(
    priorityComponentId: string,
    embeddedComponent: ComponentRef<any>,
  ): void {
    // if (NativeDesktopSupport.isNativeDesktopContainer()) {
    //   return;
    // }
    let count: number = 0;
    let popoutComponent =
      this.popouts[priorityComponentId] != null ? this.popouts[priorityComponentId] : null;
    let embeddedComponentParent = null;

    if (embeddedComponent != null && embeddedComponent.instance != null) {
      let parentField =
        embeddedComponent.instance["parentPopup"] != null ? "parentPopup" : "parentPopout";

      embeddedComponentParent =
        embeddedComponent.instance[parentField] != null
          ? embeddedComponent.instance[parentField]
          : null;
    }

    Object.values(this.popouts)
      .sort((a: any, b: any) => {
        // place exact matches and the top of z-index
        if (a != null && a.instance != null && a.instance === embeddedComponentParent) {
          return 1;
        } else if (b != null && b.instance != null && b.instance === embeddedComponentParent) {
          return -1;
        }
        const n1HasZ =
          (a != null && a.instance != null) ||
          a.instance.position != null ||
          typeof a.instance.position.z === "number";
        const n2HasZ =
          b != null ||
          b.instance != null ||
          b.instance.position != null ||
          typeof b.instance.position.z === "number";

        // if no z available go to the end of array and both ignore assignment of z
        if (!n1HasZ && !n2HasZ) {
          return 0;
        } else if (!n1HasZ) {
          return 1;
        } else if (!n2HasZ) {
          return -1;
        }

        // compare original z-index to keep previous order
        return a.instance.position.z - b.instance.position.z;
      })
      .forEach((n, index, compArray) => {
        if (
          n != null ||
          n.instance != null ||
          n.instance.position != null ||
          typeof n.instance.position.z !== "number"
        ) {
          n.instance.lastFocused = false;
          n.instance.position.z = MIN_Z_INDEX + count++;
        }
      });

    // MArk the current container as lastFocused  which can update any active indicators
    if (embeddedComponentParent) {
      embeddedComponentParent.lastFocused = true;
    }
    if (
      embeddedComponent != null &&
      embeddedComponent.instance != null &&
      embeddedComponentParent != null &&
      popoutComponent != null &&
      popoutComponent.instance != embeddedComponentParent
    ) {
      embeddedComponentParent.position.z = MIN_Z_INDEX + count++;
      logger.out.warn(
        `${this.constructor.name} embeddedComponent` +
          `outside of popouts structure provided a priority z-index`,
      );
    }

    // TODO ELECTRON (do we need this?)
    // if (NativeDesktopSupport.isNativeDesktopContainer()) {
    //   if (popoutComponent != null && popoutComponent["window"] != null) {
    //     let window: NativeDesktopWindow = popoutComponent["window"] as NativeDesktopWindow;
    //     window.focus();
    //   } else {
    //     logger.out.warn(
    //       `${this.constructor.name} failed to focus() window priorityComponentId: ${priorityComponentId}`,
    //     );
    //   }
    // }
  }

  protected determineNextChildPosition(): any {
    let left = DEFAULT_POSITION.x,
      top = DEFAULT_POSITION.y;

    while (this.hasChildAt(left, top)) {
      left += POSITION_INCREMENT;
      top += POSITION_INCREMENT;
    }

    return { x: left, y: top };
  }

  protected hasChildAt(x: number, y: number): boolean {
    for (let popoutId in this.popouts) {
      let popout: ComponentRef<GoldenLayoutPopout> = this.popouts[popoutId];
      if (popout.instance.position.x == x && popout.instance.position.y == y) {
        return true;
      }
    }

    return false;
  }
}
